import { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, Icon, Image, Text } from "@chakra-ui/react";
import twttr from "twitter-text";
import { getSocialProviderStyles } from "../../../_utils/socialProvider";
import { CARD_THEME_TYPES, CARD_THEMES } from "./snapshotMaker.constants";
import { ReactComponent as BrandTextIcon } from "../../../assets/logo/smp-logo-with-text.svg";

const addHighlights = (text) => {
  const highlighted = twttr.autoLink(text, { usernameIncludeSymbol: true });

  return highlighted;
};

const SnapshotMakerCard = ({ data = {}, config = {}, watermark }) => {
  const { t } = useTranslation(["snapshot-maker"]);
  const { name, nickname, avatarUrl, message, provider } = data;
  const { themeColor, cornerRadius, shadow, width, scale } = config;
  const providerIconStyles = useMemo(() => provider ? getSocialProviderStyles(provider) : undefined, [provider]);
  const messageHighlighted = useMemo(() => message ? addHighlights(message) : "", [message]);

  return (
    <Box
      fontSize={`${1 * (scale || 1)}em`}
      width={`${50 * (width || 1)}%`}
      padding="1em"
      borderRadius={`${1 * (cornerRadius || 0)}em`}
      background={CARD_THEMES[themeColor]?.background}
      color={CARD_THEMES[themeColor]?.color}
      boxShadow={shadow ? `0 0 ${1 * shadow}em rgba(0, 0, 0, 0.85)` : null}
      userSelect="none"
      pointerEvents="none"
    >
      {(name || nickname || avatarUrl || providerIconStyles) && (
        <Box display="flex" alignItems="center" marginBottom="0.75em">
          {avatarUrl && (
            <Image
              width="3em"
              minWidth="3em"
              height="3em"
              objectFit="cover"
              borderRadius="50%"
              overflow="hidden"
              marginRight="1em"
              src={avatarUrl}
            />
          )}
          <Box marginRight={providerIconStyles ? "0.5em" : null}>
            {name && <Box fontWeight="bold">{name}</Box>}
            {nickname && <Box fontSize="0.9em" opacity="0.5">{nickname}</Box>}
          </Box>
          {providerIconStyles && (
            <Icon
              marginLeft="auto"
              fontSize="1.5em"
              color={providerIconStyles.background}
              as={providerIconStyles.icon}
            />
          )}
        </Box>
      )}
      <Box
        overflowWrap="break-word"
        whiteSpace="pre-wrap"
        dangerouslySetInnerHTML={{
          __html: messageHighlighted,
        }}
        sx={{
          a: {
            color: CARD_THEMES[themeColor]?.messageLinkColor,
          },
        }}
      />
      {watermark && (
        <Box marginTop="0.75em" fontSize="0.8em" lineHeight="1em">
          <Text as="span" verticalAlign="middle" opacity="0.5">{t("made_with")}</Text>&nbsp;&nbsp;
          <Icon as={BrandTextIcon} width="auto" fontSize="1.25em" />
        </Box>
      )}
    </Box>
  );
};

SnapshotMakerCard.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    nickname: PropTypes.string,
    avatarUrl: PropTypes.string,
    message: PropTypes.string,
    provider: PropTypes.string,
  }),
  config: PropTypes.shape({
    themeColor: PropTypes.oneOf(Object.values(CARD_THEME_TYPES)),
    cornerRadius: PropTypes.number,
    shadow: PropTypes.number,
    width: PropTypes.number,
    scale: PropTypes.number,
  }),
  watermark: PropTypes.bool,
};

SnapshotMakerCard.defaultProps = {
  data: undefined,
  config: undefined,
  watermark: undefined,
};

export default SnapshotMakerCard;
